"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AimOfAnalysis, AudienceBackground } from "@/generated/fastapi";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import useIdentifiedTopicsState from "./useIdentifiedTopicsState";
import useIdentifyTopicsMutation from "../../mutations/useIdentifyTopicsMutation";
import useTopicsForOriginQuery from "../../queries/useTopicsForOriginQuery";

type UseIdentifyTopicsControllerParams = {
  originId: string;
};

const MOVEON_ASSOCIATED_AIMS: AimOfAnalysis[] = [
  "product_promotion",
  "user_problem",
  "tutorial_guide",
];

function shouldAssociateWithMoveOn(selectedAims: AimOfAnalysis[]) {
  return selectedAims.some((aim) => MOVEON_ASSOCIATED_AIMS.includes(aim));
}

export default function useIdentifyTopicsController({
  originId,
}: UseIdentifyTopicsControllerParams) {
  const { aimOfAnalysisItems, audienceBackgroundItems } = useOptionItems();
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [intendedMessage, setIntendedMessage] = useState("");
  const [proposedPost, setProposedPost] = useState<string | null>(null);
  const [selectedAudienceBackground, setSelectedAudienceBackground] = useState<
    AudienceBackground[]
  >([]);
  const [selectedAims, setSelectedAims] = useState<AimOfAnalysis[]>([]);

  const defaultAssociateWithMoveOn = shouldAssociateWithMoveOn(selectedAims);
  const [associateWithMoveOn, setAssociateWithMoveOn] = useState(
    defaultAssociateWithMoveOn,
  );

  useEffect(() => {
    setAssociateWithMoveOn(defaultAssociateWithMoveOn);
  }, [defaultAssociateWithMoveOn]);

  const topicsState = useIdentifiedTopicsState();
  const {
    clearTopics,
    identifiedTopics,
    hasUnconfirmedTopics,
    removeTopic,
    setTopics,
    updateTopic,
  } = topicsState;
  const selectedAim = selectedAims[0] ?? null;
  const hasUnconfirmedTopicItems = hasUnconfirmedTopics();
  const topicsQuery = useTopicsForOriginQuery({
    aim: selectedAim,
    enabled: !hasUnconfirmedTopicItems,
    onlyConfirmed: true,
    originId,
  });
  const identifyTopicsMutation = useIdentifyTopicsMutation();

  useEffect(() => {
    if (topicsQuery.data) {
      setTopics(topicsQuery.data);
    }
  }, [setTopics, topicsQuery.data]);

  useEffect(() => {
    if (topicsQuery.isError) {
      showErrorToast("Failed to load topics");
    }
  }, [topicsQuery.isError]);

  const isIdentifiedTopics = Boolean(identifiedTopics?.length);
  const loadingTopics =
    topicsQuery.isLoading || identifyTopicsMutation.isPending;
  const canIdentify = Boolean(selectedAim) && !hasUnconfirmedTopicItems;
  const disabledReason = loadingTopics
    ? "Loading topics..."
    : hasUnconfirmedTopicItems
      ? "Confirm or clear the current topics before identifying another set."
      : !selectedAim
        ? "Choose an aim before identifying topics."
        : null;
  const hasCustomOptions = Boolean(
    selectedAudienceBackground.length > 0 ||
      intendedMessage.trim().length > 0 ||
      associateWithMoveOn !== defaultAssociateWithMoveOn,
  );

  const drawerInfoItems = useMemo(
    () => [
      { label: `Source ${originId.slice(0, 8)}`, colorPalette: "blue" },
      {
        label: selectedAim ?? "Choose aim",
        colorPalette: selectedAim ? "purple" : "gray",
      },
      {
        label: isIdentifiedTopics
          ? `${identifiedTopics?.length ?? 0} topics`
          : "No topics yet",
        colorPalette: isIdentifiedTopics ? "green" : "gray",
      },
    ],
    [identifiedTopics?.length, isIdentifiedTopics, originId, selectedAim],
  );

  const handleToggleOptions = useCallback(() => {
    setIsOptionsOpen((isOpen) => !isOpen);
  }, []);

  const removeAllTopicsFromLocalList = useCallback(() => {
    clearTopics();
    setSelectedAims([]);
  }, [clearTopics]);

  const handleIdentify = useCallback(async () => {
    if (!selectedAim || hasUnconfirmedTopicItems) return;

    try {
      const response = await identifyTopicsMutation.mutateAsync({
        origin_id: originId,
        aim: selectedAim,
        associate_with_moveon: associateWithMoveOn,
        intended_message:
          intendedMessage.trim().length > 0
            ? intendedMessage.trim()
            : undefined,
        audience_background:
          selectedAudienceBackground.length > 0
            ? selectedAudienceBackground[0]
            : undefined,
      });

      if (response.status === "success") {
        setTopics(response.identified_topics || []);
        showSuccessToast(
          `${response.count_topics_identified} topics identified`,
        );
        return;
      }

      setTopics([]);
      showSuccessToast(response.message || "No topics identified");
    } catch {
      showErrorToast("Failed to identify topics");
    }
  }, [
    associateWithMoveOn,
    hasUnconfirmedTopicItems,
    identifyTopicsMutation,
    intendedMessage,
    originId,
    selectedAim,
    selectedAudienceBackground,
    setTopics,
  ]);

  return {
    drawerInfoItems,
    controls: {
      aimItems: aimOfAnalysisItems,
      canIdentify,
      disabledReason,
      handleIdentify,
      handleToggleOptions,
      hasCustomOptions,
      isIdentifying: loadingTopics,
      selectedAims,
      setSelectedAims,
    },
    options: {
      associateWithMoveOn,
      audienceBackgroundItems,
      intendedMessage,
      isOpen: isOptionsOpen,
      selectedAudienceBackground,
      setAssociateWithMoveOn,
      setIntendedMessage,
      setIsOpen: setIsOptionsOpen,
      setSelectedAudienceBackground,
    },
    review: {
      hasUnconfirmedTopics: hasUnconfirmedTopicItems,
      identifiedTopics,
      isIdentifiedTopics,
      proposedPost,
      removeAllTopicsFromLocalList,
      removeTopic,
      setProposedPost,
      updateTopic,
    },
    status: {
      isLoading: loadingTopics,
    },
  };
}
